import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon, QuoteIcon, StarIcon } from "./icons";

interface Testimonial {
  text: string;
  author: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    text: "I type in a rough idea and instantly get a full lesson plan, ready to teach. What used to take me a whole evening now takes minutes.",
    author: "Ellie",
  },
  {
    text: "It actually knows my syllabus, not just vague general knowledge. The outcomes and content descriptors make sure my learning intentions are properly aligned to what I need to teach.",
    author: "Gen",
  },
  {
    text: "I love that it follows the explicit teaching model, with clear I do, we do, you do, and checks for understanding. It's exactly how our school expects us to teach.",
    author: "Liam",
  },
  {
    text: "Plans change constantly and this keeps up. I can tweak a lesson, swap an activity or reshuffle the whole unit in seconds without starting over.",
    author: "Will",
  },
  {
    text: "The slideshows are the best part. I just upload my program and the whole week's slides are done.",
    author: "Antoinette",
  },
  {
    text: "The differentiation ideas are actually fantastic. Now every lesson comes with support and extension built in, so all my kids are catered for.",
    author: "Daryl",
  },
  {
    text: "I LOVE this tool for making personalised plans. It literally helps me be a better teacher by suggesting great goals and strategies.",
    author: "Kate",
  },
  {
    text: "This is such a valuable tool for teachers. It's amazing - easy to use, clear links to the syllabus, and I love the break down of the lessons.",
    author: "Kiri",
  },
  {
    text: "As a casual, this is amazing! Lesson plans in seconds mean I can be ready for any stage level and any subject at the drop of a hat, no stress.",
    author: "Lauren",
  },
];

interface TestimonialsSectionProps {
  isMobile: boolean;
}

export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ isMobile }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(true);

  const syncArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    // 1px tolerance: fractional card widths leave sub-pixel scroll remainders at the ends.
    setCanScrollBack(track.scrollLeft > 1);
    setCanScrollForward(track.scrollLeft < track.scrollWidth - track.clientWidth - 1);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    // Observe the track rather than the window: on first paint the cards have not been
    // laid out yet, so a one-off measurement reports nothing to scroll to.
    const observer = new ResizeObserver(syncArrows);
    observer.observe(track);
    return () => observer.disconnect();
  }, [syncArrows]);

  const scrollByCard = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.firstElementChild as HTMLElement | null;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = card ? card.offsetWidth + gap : track.clientWidth;
    track.scrollBy({ left: step * direction, behavior: "smooth" });
  };

  return (
    <section className="testimonials-section">
      <div className="testimonials-content">
        <h2 className="testimonials-heading">What Teachers Are Saying</h2>
        <div className="testimonials-carousel" ref={trackRef} onScroll={syncArrows}>
          {TESTIMONIALS.map(({ text, author }) => (
            <div key={author} className="testimonial-card">
              <div className="testimonial-quote-icon">
                <QuoteIcon />
              </div>
              <p className="testimonial-text">{text}</p>
              <div className="testimonial-footer">
                <div className="testimonial-stars">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} />
                  ))}
                </div>
                <span className="testimonial-author">{author}</span>
              </div>
            </div>
          ))}
        </div>
        {!isMobile && (
          <div className="testimonials-controls">
            <button
              type="button"
              className="testimonials-arrow"
              onClick={() => scrollByCard(-1)}
              disabled={!canScrollBack}
              aria-label="Previous testimonials"
            >
              <ChevronLeftIcon />
            </button>
            <button
              type="button"
              className="testimonials-arrow"
              onClick={() => scrollByCard(1)}
              disabled={!canScrollForward}
              aria-label="Next testimonials"
            >
              <ChevronRightIcon />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
